import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as invitationsApi from '~/services/api/invitations';
import * as organizationsApi from '~/services/api/organizations';

import type { OrganizationMember, OrgRole } from '~/types';

import { useAuth } from './useAuth';

/**
 * Query key factory for members.
 */
export const memberKeys = {
  all: ['members'] as const,
  list: (orgId: string) => ['members', 'list', orgId] as const,
  lists: () => ['members', 'list'] as const,
};

/**
 * Invite a member to an organization.
 */
export function useInviteMember(): ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof invitationsApi.sendInvitation>>,
    Error,
    { email: string; orgId: string; role: 'admin' | 'member' }
  >
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, orgId, role }) => invitationsApi.sendInvitation(orgId, { email, role }),
    onSuccess: (_data, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.list(orgId) });
    },
  });
}

/**
 * Fetch members for an organization.
 */
export function useOrganizationMembers(orgId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  members: OrganizationMember[];
} {
  const {
    data: members = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: memberKeys.list(orgId ?? ''),
    queryFn: () => (orgId ? organizationsApi.getOrganizationMembers(orgId) : []),
    enabled: !!orgId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return { error, isLoading, members };
}

/**
 * Remove a member from an organization.
 */
export function useRemoveMember(): ReturnType<
  typeof useMutation<void, Error, { orgId: string; userId: string }>
> {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ orgId, userId }) => organizationsApi.removeMember(orgId, userId),
    onSuccess: (_data, { orgId, userId }) => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.list(orgId) });

      // If the current user was removed, invalidate their organizations list
      // so the org selector updates (org disappears if removed, or appears if added back)
      // Matches TenantContext query key: ['organization', 'current']
      if (user?.uid === userId) {
        void queryClient.invalidateQueries({ queryKey: ['organization'] });
      }
    },
  });
}

/**
 * Resend an invitation.
 */
export function useResendInvitation(): ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof invitationsApi.resendInvitation>>,
    Error,
    { invitationId: string; orgId: string }
  >
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId }) => invitationsApi.resendInvitation(invitationId),
    onSuccess: (_data, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.list(orgId) });
    },
  });
}

/**
 * Update a member's role.
 */
export function useUpdateMemberRole(): ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof organizationsApi.updateMemberRole>>,
    Error,
    { orgId: string; role: OrgRole; userId: string }
  >
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, role, userId }) => organizationsApi.updateMemberRole(orgId, userId, role),
    onSuccess: (_data, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: memberKeys.list(orgId) });
    },
  });
}
